import type {
  ActiveFilter,
  FilterDefinition,
} from '@/views/Order/components/table/OrderToolbar';
import { MULTI_VALUE_SEPARATOR } from '@/views/Order/components/table/TableFacetedFilter';
import {
  formatLocalDay,
  parseDateRangeValue,
} from '@/views/Order/utils/orderListFilters';
import { PickingJobSegment, PickingJobStatus } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';
import {
  pickingJobSegmentLabel,
  pickingJobStatusLabel,
} from './pickingJobLabels';
import type { PickingJobRow, WarehouseOption } from './types';

/** The faceted filter's "nothing selected" value. */
export const FILTER_UNSET = '__all__';

/**
 * Every filter is a column and shares its id: sorting, column visibility and
 * the toolbar's column labels all key on these. They are also persisted, so
 * renaming one drops what users have stored for it.
 */
export const COLUMN_IDS = {
  warehouse: 'warehouse',
  status: 'status',
  pickupDate: 'carrier_pickup_date',
  segment: 'segment',
  destination: 'destination',
  job: 'id',
  age: 'age',
  picked: 'picked',
} as const;

/** Shown without being added; the three the server query is scoped by. */
export const DEFAULT_FILTER_IDS: string[] = [
  COLUMN_IDS.warehouse,
  COLUMN_IDS.status,
  COLUMN_IDS.pickupDate,
];

/**
 * Every status the filter offers, and the set the query falls back to when
 * nothing is selected — so a status missing here is a job the overview can
 * never show, whatever the user picks.
 *
 * RELEASED belongs in it for exactly that reason: a released job is the record
 * of somebody's intervention, and the overview is where it has to stay
 * findable afterwards. It is deliberately not in DEFAULT_STATUSES, which is
 * what the page opens with — resolved work is not work.
 */
export const ALL_STATUSES: PickingJobStatus[] = [
  PickingJobStatus.PickingNotStarted,
  PickingJobStatus.PickingInProgress,
  PickingJobStatus.Picked,
  PickingJobStatus.DeliveryInProgress,
  PickingJobStatus.DeliveryIncomplete,
  PickingJobStatus.Delivered,
  PickingJobStatus.Released,
];

/**
 * "Not delivered yet": every real job that is still open, whether it is stuck
 * in picking or after it. This is the default status filter, and clearing the
 * status filter resets it to this set — PICKING_NOT_STARTED entries are virtual
 * aggregates and DELIVERED grows forever, so both are only queried when the
 * user picks them explicitly.
 */
export const DEFAULT_STATUSES: PickingJobStatus[] = [
  PickingJobStatus.PickingInProgress,
  PickingJobStatus.Picked,
  PickingJobStatus.DeliveryInProgress,
  PickingJobStatus.DeliveryIncomplete,
];

/**
 * How far back the DELIVERED query looks when no pickup start date is set.
 * Without it, one click would fetch every job ever delivered, per warehouse,
 * with its items and orders nested.
 */
export const DELIVERED_WINDOW_DAYS = 30;

/**
 * A job older than this is called out in the table. "Older than" in both
 * places: a highlighted row always falls in the matching age bucket.
 */
export const STUCK_THRESHOLD_DAYS = 14;

/** Age buckets, in days. One of them is the stuck threshold. */
const AGE_BUCKETS = [7, STUCK_THRESHOLD_DAYS, 30, 90];

export function splitValues(value: string): string[] {
  if (!value || value === FILTER_UNSET) return [];
  return value.split(MULTI_VALUE_SEPARATOR).filter(Boolean);
}

export function joinValues(values: string[]): string {
  return values.length > 0 ? values.join(MULTI_VALUE_SEPARATOR) : FILTER_UNSET;
}

/**
 * The value a filter is stored with. A cleared status filter is stored as
 * DEFAULT_STATUSES, never empty: DELIVERED and PICKING_NOT_STARTED are only
 * queried when picked explicitly, and the facet must show the statuses that
 * are actually loaded rather than "all".
 */
export function normalizeFilterValue(id: string, value: string): string {
  return id === COLUMN_IDS.status && splitValues(value).length === 0
    ? joinValues(DEFAULT_STATUSES)
    : value;
}

/**
 * Whether a stored status value selects exactly DEFAULT_STATUSES. Compared as
 * a set: the facet stores the ticked values in click order, so unticking and
 * reticking one status gives the same selection a different spelling.
 */
export function isDefaultStatusSelection(value: string): boolean {
  const selected = new Set(splitValues(value));
  return (
    selected.size === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every((status) => selected.has(status))
  );
}

export function filterValue(filters: ActiveFilter[], id: string): string {
  return filters.find((f) => f.id === id)?.value ?? FILTER_UNSET;
}

export function destinationLabel(
  row: Pick<PickingJobRow, 'destination'>,
): string {
  return (
    row.destination.event?.name ??
    row.destination.partner_name ??
    row.destination.drop_off_location?.name ??
    '-'
  );
}

/** Progress, not volume: a 2/2 job is "more picked" than a 50/200 one. */
export function pickedRatio(row: PickingJobRow): number | null {
  return row.total_amount_ordered > 0
    ? row.total_amount_picked / row.total_amount_ordered
    : null;
}

/**
 * What the destination facet filters on. Events and drop-off locations are
 * keyed by id so two of the same name stay apart; a partner is only known by
 * name here, so same-named partners still share one option.
 */
export function destinationKey(row: PickingJobRow): string {
  if (row.destination.event?.id) return `event:${row.destination.event.id}`;
  if (row.destination.partner_name) {
    return `partner:${row.destination.partner_name}`;
  }
  if (row.destination.drop_off_location?.id) {
    return `dropoff:${row.destination.drop_off_location.id}`;
  }
  return '';
}

/** The facets whose options come from the loaded rows. */
const ROW_FACETS: Record<
  string,
  {
    toValue: (row: PickingJobRow) => string;
    toLabel: (row: PickingJobRow) => string;
  }
> = {
  [COLUMN_IDS.destination]: {
    toValue: destinationKey,
    toLabel: destinationLabel,
  },
  [COLUMN_IDS.job]: {
    toValue: (row) => row.id,
    toLabel: (row) => row.id.slice(0, 8),
  },
};

/** The last label seen for each value of a row-derived facet, by facet id. */
export type RowFacetLabels = Record<string, ReadonlyMap<string, string>>;

/**
 * `known` with the labels `rows` carry added (a newer label replaces an
 * older one). The same object when nothing changed, so it can back state.
 */
export function rememberRowFacetLabels(
  known: RowFacetLabels,
  rows: PickingJobRow[],
): RowFacetLabels {
  let changed = false;
  const next: Record<string, Map<string, string>> = {};
  Object.entries(ROW_FACETS).forEach(([facetId, { toValue, toLabel }]) => {
    const labels = new Map(known[facetId] ?? []);
    rows.forEach((row) => {
      const value = toValue(row);
      if (!value) return;
      const label = toLabel(row);
      if (labels.get(value) !== label) {
        labels.set(value, label);
        changed = true;
      }
    });
    next[facetId] = labels;
  });
  return changed ? next : known;
}

/** Row facet labels as stored: facet id → value → label. */
type StoredFacetLabels = Record<string, Record<string, string>>;

/**
 * The labels of the row-facet values the filters select, for storage next to
 * the filters: after a reload they label a selected value no loaded row
 * carries. Only the selected ones, so what is stored stays as small as the
 * filters.
 */
export function selectedFacetLabels(
  known: RowFacetLabels,
  filters: ActiveFilter[],
): StoredFacetLabels {
  const stored: StoredFacetLabels = {};
  Object.keys(ROW_FACETS).forEach((facetId) => {
    const labels: Record<string, string> = {};
    splitValues(filterValue(filters, facetId)).forEach((value) => {
      const label = known[facetId]?.get(value);
      if (label != null) labels[value] = label;
    });
    if (Object.keys(labels).length > 0) stored[facetId] = labels;
  });
  return stored;
}

/**
 * What selectedFacetLabels stored, validated: anything but a string label of
 * a known row facet is dropped.
 */
export function readFacetLabels(stored: unknown): RowFacetLabels {
  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
  if (!isRecord(stored)) return {};
  const known: Record<string, Map<string, string>> = {};
  Object.keys(ROW_FACETS).forEach((facetId) => {
    const entries = stored[facetId];
    if (!isRecord(entries)) return;
    const labels = new Map(
      Object.entries(entries).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === 'string' && entry[1] !== '',
      ),
    );
    if (labels.size > 0) known[facetId] = labels;
  });
  return known;
}

/**
 * A row-derived facet's options: every value the rows carry, plus every
 * selected value they do not. The rows change with every load, and a selected
 * value missing from the options would still filter the table with nothing in
 * the facet to show or clear it. Such a value keeps the last label seen for
 * it — stored with the filters, so across a reload too — or shows its raw
 * value when it has never been seen.
 */
function rowFacetOptions(
  facetId: string,
  rows: PickingJobRow[],
  filters: ActiveFilter[],
  knownLabels: RowFacetLabels,
) {
  const { toValue, toLabel } = ROW_FACETS[facetId];
  const seen = new Map<string, string>();
  rows.forEach((row) => {
    const value = toValue(row);
    if (value && !seen.has(value)) seen.set(value, toLabel(row));
  });
  splitValues(filterValue(filters, facetId)).forEach((value) => {
    if (!seen.has(value)) {
      seen.set(value, knownLabels[facetId]?.get(value) ?? value);
    }
  });
  return [...seen.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([value, label]) => ({ value, getLabel: () => label }));
}

export function getPickingJobFilterDefinitions(
  warehouses: WarehouseOption[],
  rows: PickingJobRow[],
  filters: ActiveFilter[] = [],
  knownLabels: RowFacetLabels = {},
): FilterDefinition[] {
  return [
    {
      id: COLUMN_IDS.warehouse,
      getLabel: () => i18n.t('Picking:columnWarehouse'),
      isDefault: true,
      isRemovable: false,
      multiSelect: true,
      options: warehouses.map((w) => ({
        value: w.itemLocationId,
        getLabel: () => w.name,
      })),
    },
    {
      id: COLUMN_IDS.status,
      getLabel: () => i18n.t('Picking:columnStatus'),
      isDefault: true,
      isRemovable: false,
      multiSelect: true,
      options: ALL_STATUSES.map((status) => ({
        value: status,
        getLabel: () => pickingJobStatusLabel(status),
      })),
    },
    {
      id: COLUMN_IDS.pickupDate,
      getLabel: () => i18n.t('Picking:columnPickupDate'),
      kind: 'dateRange',
      isDefault: true,
      isRemovable: false,
      options: [],
    },
    {
      id: COLUMN_IDS.segment,
      getLabel: () => i18n.t('Picking:columnSegment'),
      multiSelect: true,
      options: [PickingJobSegment.OpenSystem, PickingJobSegment.Event].map(
        (segment) => ({
          value: segment,
          getLabel: () => pickingJobSegmentLabel(segment),
        }),
      ),
    },
    {
      id: COLUMN_IDS.destination,
      getLabel: () => i18n.t('Picking:columnDestination'),
      multiSelect: true,
      options: rowFacetOptions(
        COLUMN_IDS.destination,
        rows,
        filters,
        knownLabels,
      ),
    },
    {
      id: COLUMN_IDS.job,
      getLabel: () => i18n.t('Picking:columnJob'),
      multiSelect: true,
      options: rowFacetOptions(COLUMN_IDS.job, rows, filters, knownLabels),
    },
    {
      id: COLUMN_IDS.age,
      getLabel: () => i18n.t('Picking:columnAge'),
      options: AGE_BUCKETS.map((days) => ({
        value: String(days),
        getLabel: () => i18n.t('Picking:filterOlderThan', { days }),
      })),
    },
    {
      id: COLUMN_IDS.picked,
      getLabel: () => i18n.t('Picking:columnPicked'),
      options: [
        {
          value: 'complete',
          getLabel: () => i18n.t('Picking:filterFullyPicked'),
        },
        {
          value: 'incomplete',
          getLabel: () => i18n.t('Picking:filterNotFullyPicked'),
        },
      ],
    },
  ];
}

/**
 * Applies the filters the server query does not cover. Warehouse, status and
 * pickup date are query arguments; everything else narrows the merged result
 * set here.
 */
export function applyClientFilters(
  rows: PickingJobRow[],
  filters: ActiveFilter[],
): PickingJobRow[] {
  const segments = splitValues(filterValue(filters, COLUMN_IDS.segment));
  const destinations = splitValues(
    filterValue(filters, COLUMN_IDS.destination),
  );
  const jobIds = splitValues(filterValue(filters, COLUMN_IDS.job));
  const [minAge] = splitValues(filterValue(filters, COLUMN_IDS.age));
  const [pickedState] = splitValues(filterValue(filters, COLUMN_IDS.picked));

  return rows.filter((row) => {
    if (segments.length > 0 && !segments.includes(row.segment)) return false;
    if (
      destinations.length > 0 &&
      !destinations.includes(destinationKey(row))
    ) {
      return false;
    }
    if (jobIds.length > 0 && !jobIds.includes(row.id)) return false;
    if (minAge) {
      // "Older than N days" excludes N itself.
      if (row.ageInDays === null || row.ageInDays <= Number(minAge))
        return false;
    }
    if (pickedState) {
      const complete = row.total_amount_picked >= row.total_amount_ordered;
      if (pickedState === 'complete' && !complete) return false;
      if (pickedState === 'incomplete' && complete) return false;
    }
    return true;
  });
}

/**
 * Earliest date the overview ever asks for. getPickingJobs defaults *each*
 * missing bound to today, so omitting the dates means "today only" rather than
 * "no restriction". An unset pickup-date filter therefore sends a range wide
 * enough to be unbounded in practice: picking jobs only exist from 2026 on.
 */
const UNBOUNDED_FROM = '2020-01-01';
const UNBOUNDED_TO_YEARS_AHEAD = 2;

function unboundedTo(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + UNBOUNDED_TO_YEARS_AHEAD);
  return formatLocalDay(d);
}

function deliveredWindowFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - DELIVERED_WINDOW_DAYS);
  return formatLocalDay(d);
}

/**
 * The pickup-date filter value translated into the query's date arguments.
 * An unset bound means every date, not today; see UNBOUNDED_FROM. `floor`
 * replaces that default start when the user set none.
 */
export function pickupDateArgs(
  filters: ActiveFilter[],
  floor: string = UNBOUNDED_FROM,
): {
  from: string;
  to: string;
} {
  const { from, to } = parseDateRangeValue(
    filterValue(filters, COLUMN_IDS.pickupDate),
  );
  return {
    from: from ? formatLocalDay(from) : floor,
    to: to ? formatLocalDay(to) : unboundedTo(),
  };
}

/**
 * Whether DELIVERED is floored at DELIVERED_WINDOW_DAYS: it is selected and
 * the user has not set a start date of their own.
 */
export function deliveredFloorApplies(
  filters: ActiveFilter[],
  statuses: PickingJobStatus[],
): boolean {
  return (
    statuses.includes(PickingJobStatus.Delivered) &&
    !parseDateRangeValue(filterValue(filters, COLUMN_IDS.pickupDate)).from
  );
}

export type PickingJobQueryArgs = {
  statuses: PickingJobStatus[];
  from: string;
  to: string;
};

/**
 * The queries one warehouse needs. colugo applies the date bounds to every
 * status in a query, so the DELIVERED floor gets a query of its own: the open
 * statuses must never be cut off by it. Without the floor all statuses share
 * one query.
 */
export function pickingJobQueries(
  filters: ActiveFilter[],
  statuses: PickingJobStatus[],
): PickingJobQueryArgs[] {
  if (!deliveredFloorApplies(filters, statuses)) {
    return [{ statuses, ...pickupDateArgs(filters) }];
  }
  const delivered: PickingJobQueryArgs = {
    statuses: [PickingJobStatus.Delivered],
    ...pickupDateArgs(filters, deliveredWindowFrom()),
  };
  const others = statuses.filter((s) => s !== PickingJobStatus.Delivered);
  return others.length === 0
    ? [delivered]
    : [{ statuses: others, ...pickupDateArgs(filters) }, delivered];
}
